/**
 * File Settings Modal TypeScript
 * 
 * File sharing modal integrated into VM User Gallery using WC Backbone Modal
 * Adapted from File Settings Modal for individual file permissions
 */

// Import WC Backbone Modal
import { VMWCBackboneModal } from '../../../vm-product-cpt/public/dev_source/admin/wc-backone-modal';
import { SpinnerController } from '../../../../public/assets/ts/spinner_controller';

// Type declarations
declare var jQuery: any;
declare var vmFileSettings: {
    ajaxUrl: string;
    nonce: string;
    driveSettingsNonce: string;
    currentUser: any;
    strings: {
        loading: string;
        save: string;
        cancel: string;
        done: string;
        addPeople: string;
        peopleWithAccess: string;
        systemUsersWithAccess: string;
        generalAccess: string;
        exceptions: string;
        restricted: string;
        anyoneWithLink: string;
        viewer: string;
        contributor: string;
        editor: string;
        removePermissions: string;
        useFolderPermissions: string;
        manageAccess?: string;
        moveToTrash?: string;
        confirmTrash?: string;
    };
};

interface DriveEntity {
    id: string;
    name: string;
    email?: string;
    role: 'viewer' | 'contributor' | 'editor' | 'admin';
    can_remove: boolean;
    can_edit: boolean;
    avatar?: string;
    type: 'user' | 'system_role' | 'taxonomy_group' | 'secondary_role' | 'member_role' | 'profile_owner';
    description?: string;
    badge?: string;
    member_count?: number; // For groups/roles
}

interface FileSettings {
    file_id: number;
    file_name: string;
    file_type: string;
    file_size: string;
    file_owner?: DriveEntity;
    drive_type?: string;
    entities: DriveEntity[];
    general_access: 'restricted' | 'anyone_with_link' | 'logged_in_users' | 'active_members';
    general_access_entities: DriveEntity[];
    exceptions: DriveEntity[];
    has_custom_permissions: boolean;
    // Additional properties for permission control
    general_access_readonly?: boolean;
    general_access_show_add_button?: boolean;
    general_access_description?: string;
    general_access_force_viewer?: boolean;
    general_access_options?: {
        [key: string]: string;
    };
    general_access_descriptions?: {
        [key: string]: string;
    };
    show_exceptions?: boolean;
    exceptions_description?: string;
}

(function($: any) {
    'use strict';

    class FileSettingsModal {
        private modal: VMWCBackboneModal;
        private currentSettings: FileSettings | null = null;
        private isOpen: boolean = false;
        private spinnerController: SpinnerController;
        /** Stored reference for admin grid contextmenu handler so it can be removed to avoid leaks. */
        private _adminGridContextMenuHandler: ((e: MouseEvent) => void) | null = null;

        constructor() {
            this.modal = new VMWCBackboneModal('vm-file-settings-modal');
			this.spinnerController = new SpinnerController();
            this.init();
        }

        private getString(key: string, fallback: string = ''): string {
            return (vmFileSettings?.strings as any)?.[key] || fallback;
        }

        private init(): void {
            this.bindEvents();
            this.setupModalTriggers();
        }

        private bindEvents(): void {
            // Unbind any existing events first to prevent double-binding
            $(document).off('click', '#vm-file-settings-modal .custom-wc-modal-close');
            $(document).off('click', '#vm-file-settings-modal .wc-backbone-modal-backdrop');
            $(document).off('click', '#vm-file-settings-modal #vm-drive-save-btn');
            $(document).off('click', '#vm-file-settings-modal #vm-file-cancel-btn');
            $(document).off('click', '#vm-file-settings-modal .vm-remove-user');
            $(document).off('click', '#vm-file-settings-modal .vm-add-people-btn');
            $(document).off('click', '#vm-file-settings-modal .vm-add-to-exceptions-btn');
            $(document).off('change', '#vm-file-settings-modal #vm-general-access-select');
            $(document).off('input', '#vm-file-settings-modal .vm-user-search-input');
            $(document).off('click', '#vm-file-settings-modal .vm-search-result-item');
            
            // Modal close events - use document delegation for WC modal
            $(document).on('click', '#vm-file-settings-modal .custom-wc-modal-close, #vm-file-settings-modal .wc-backbone-modal-backdrop', () => {
                this.close();
            });

            // Save button
            $(document).on('click', '#vm-file-settings-modal #vm-drive-save-btn', () => {
                this.saveSettings();
            });

            // Cancel button  
            $(document).on('click', '#vm-file-settings-modal #vm-file-cancel-btn', () => {
                this.close();
            });

            // Remove entity
            $(document).on('click', '#vm-file-settings-modal .vm-remove-user', (e: any) => {
                e.preventDefault();
                e.stopPropagation();
                const entityId = $(e.target).data('user-id');
                const context = $(e.target).data('context');
                this.removeEntity(entityId, context);
            });

            // Add People button click handler
            $(document).on('click', '#vm-file-settings-modal .vm-add-people-btn', (e: any) => {
                const context = $(e.target).data('context');
                this.toggleSearchContainer(context);
            });

            // Add Account Holder to Exceptions button
            $(document).on('click', '#vm-file-settings-modal .vm-add-to-exceptions-btn', (e: any) => {
                const entityId = $(e.target).data('user-id');
                const context = $(e.target).data('context');
                this.addProfileOwnerToExceptions(entityId, context);
            });

            // General access dropdown change
            $(document).on('change', '#vm-file-settings-modal #vm-general-access-select', (e: any) => {
                const newValue = $(e.target).val();
                if (this.currentSettings) {
                    this.currentSettings.general_access = newValue as FileSettings['general_access'];
                }
                this.updateGeneralAccessDescription(newValue);
                this.toggleCopyLinkButton(newValue);
            });

            // Copy file link button
            $(document).on('click', '#vm-file-settings-modal #vm-copy-file-link-btn', (e: any) => {
                e.preventDefault();
                this.copyFileLink();
            });

            // User search functionality
            $(document).on('input', '#vm-file-settings-modal .vm-user-search-input', (e: any) => {
                const searchTerm = $(e.target).val().toString().trim();
                const context = $(e.target).data('context');
                
                if (searchTerm.length >= 2) {
                    this.searchUsers(searchTerm, context);
                } else {
                    // Hide only the results for this specific context
                    $(`.vm-search-results[data-context="${context}"]`).hide().empty();
                }
            });

            // Search result clicks
            $(document).on('click', '#vm-file-settings-modal .vm-search-result-item', (e: any) => {
                e.preventDefault();
                const $item = $(e.currentTarget);
                const entityData = {
                    id: $item.data('entity-id'),
                    name: $item.data('entity-name'),
                    email: $item.data('entity-email'),
                    type: $item.data('entity-type'),
                    avatar: $item.data('entity-avatar'),
                    description: $item.data('entity-description'),
                    badge: $item.data('entity-badge'),
                    member_count: $item.data('member-count')
                };
                const context = $item.data('context');
                
                this.addEntityToAccess(entityData, context);
                this.clearSearch(context);
            });

            // Intercept wrong file AJAX for my_files/org_drive and reroute to folder modal
            $(document).off('ajaxSend.vm-file-folder-reroute');
            $(document).on('ajaxSend.vm-file-folder-reroute', (_evt: any, xhr: any, settings: any) => {
                try {
                    const dataStr: string = typeof settings?.data === 'string' ? settings.data : '';
                    if (!dataStr || dataStr.indexOf('action=get_file_settings') === -1) return;
                    const params = new URLSearchParams(dataStr);
                    const fileId = params.get('file_id') || '';
                    const folderId = params.get('drive') || '';
                    const isNumeric = (v: string) => /^\d+$/.test(String(v));
                    if ((fileId === 'my_files' || fileId === 'org_drive') && isNumeric(folderId) && (window as any).driveSettingsModal) {
                        try { xhr?.abort?.(); } catch {}
                        (window as any).driveSettingsModal.open(fileId, parseInt(folderId, 10));
                        // Close the file modal spinner if it was opened by mistake
                        try { (window as any).fileSettingsModal?.close?.(); } catch {}
                    }
                } catch {/* noop */}
            });

            // Escape key to close
            $(document).on('keydown', (e: any) => {
                if (e.key === 'Escape' && this.isOpen) {
                    this.close();
                }
            });
        }

        private setupModalTriggers(): void {
            // Remove admin handlers first so setup is idempotent (no duplicate listeners)
            $(document).off('click.vm-file-manage-access');
            this.removeAdminGridContextMenuListener();

            // Folder breadcrumb Manage Access is handled by drive-settings-modal.ts only.
            // A duplicate handler here opened the file modal for my_files and leaked body scroll lock.

            // Admin RML tree: open folder modal on right-click for my_files/org_drive (not shared)
            $(document).off('contextmenu.vm-rml-folder');
            $(document).on('contextmenu.vm-rml-folder', '.rml-tree [data-id][data-type="folder"]', (e: any) => {
                const $n = $(e.currentTarget);
                const folderId = parseInt(String($n.attr('data-id') || 0), 10);
                const drive = this.getCurrentDriveType();
                if (folderId > 0 && (drive === 'my_files' || drive === 'org_drive') && (window as any).driveSettingsModal) {
                    e.preventDefault();
                    e.stopPropagation();
                    (window as any).driveSettingsModal.open(drive, folderId);
                }
            });

            // Admin list mode: Manage Access row action click
            $(document).on('click.vm-file-manage-access', '.vm-file-manage-access', (e: any) => {
                e.preventDefault();
                const $link = $(e.currentTarget);
                const fileId = parseInt(String($link.data('file-id') || 0), 10);
                const drive = String($link.data('drive') || '');
                if (fileId > 0 && (drive === 'my_files' || drive === 'org_drive' || drive === 'user_files')) {
                    this.open(fileId, drive);
                }
            });

            // Admin grid mode: right-click context menu on attachment items.
            // Use native listener with capture phase; handler is stored so it can be removed.
            if (typeof document.addEventListener === 'function') {
                this._adminGridContextMenuHandler = (e: MouseEvent) => {
                    const target = e.target as HTMLElement;
                    if (!target) return;
                    const $attachment = $(target).closest('.attachment[data-id]');
                    if ($attachment.length === 0) return;
                    if ($attachment.closest('.media-modal, .rml-tree').length > 0) return;

                    const drive = this.getCurrentDriveType();
                    if (drive === 'media_gallery' || drive === 'shared') return;
                    if (drive === 'trash' && this.isAdminTrashInsideTrashedFolder()) return;

                    e.preventDefault();
                    e.stopPropagation();

                    const fileId = parseInt(String($attachment.attr('data-id') || 0), 10);
                    if (fileId <= 0) return;

                    $('.vm-file-context-menu').remove();

                    let menuItems = '';
                    if (drive === 'trash') {
                        menuItems = `
                            <div class="vm-permission-dropdown-option vm-file-ctx-restore" data-file-id="${fileId}">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/>
                                </svg>
                                <span>Restore</span>
                            </div>
                            <div class="vm-permission-dropdown-option vm-file-ctx-delete-permanently" data-file-id="${fileId}" style="color:#d63638;">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                                </svg>
                                <span>Delete Permanently</span>
                            </div>
                        `;
                    } else {
                        menuItems = `
                            <div class="vm-permission-dropdown-option vm-file-ctx-manage-access" data-file-id="${fileId}" data-drive="${drive}">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
                                </svg>
                                <span>${vmFileSettings?.strings?.manageAccess || 'Manage Access'}</span>
                            </div>
                            <div class="vm-permission-dropdown-option vm-file-ctx-move-to-trash" data-file-id="${fileId}" style="color:#d63638;">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                                </svg>
                                <span>${vmFileSettings?.strings?.moveToTrash || 'Move to Trash'}</span>
                            </div>
                        `;
                    }

                    const menu = $(`
                        <div class="vm-file-context-menu vm-permission-dropdown" style="position:fixed; z-index:160001;">
                            ${menuItems}
                        </div>
                    `);

                    menu.css({ top: e.clientY + 'px', left: e.clientX + 'px' });
                    $('body').append(menu);

                    menu.on('click', '.vm-file-ctx-manage-access', () => {
                        const fId = parseInt(String(menu.find('.vm-file-ctx-manage-access').data('file-id') || 0), 10);
                        const dr = String(menu.find('.vm-file-ctx-manage-access').data('drive') || '');
                        menu.remove();
                        if (fId > 0) {
                            this.open(fId, dr);
                        }
                    });

                    menu.on('click', '.vm-file-ctx-move-to-trash', () => {
                        const fId = parseInt(String(menu.find('.vm-file-ctx-move-to-trash').data('file-id') || 0), 10);
                        menu.remove();
                        if (fId > 0) {
                            this.confirmAndTrashFile(fId);
                        }
                    });

                    menu.on('click', '.vm-file-ctx-restore', () => {
                        const fId = parseInt(String(menu.find('.vm-file-ctx-restore').data('file-id') || 0), 10);
                        menu.remove();
                        if (fId > 0) {
                            this.confirmAndRestoreFile(fId);
                        }
                    });

                    menu.on('click', '.vm-file-ctx-delete-permanently', () => {
                        const fId = parseInt(String(menu.find('.vm-file-ctx-delete-permanently').data('file-id') || 0), 10);
                        menu.remove();
                        if (fId > 0) {
                            this.confirmAndDeletePermanently(fId);
                        }
                    });

                    $(document).one('click.vm-file-ctx', () => {
                        menu.remove();
                    });
                };
                document.addEventListener('contextmenu', this._adminGridContextMenuHandler, true);
            }
        }

        /** Remove the native admin grid contextmenu listener to prevent leaks. */
        private removeAdminGridContextMenuListener(): void {
            if (this._adminGridContextMenuHandler && typeof document.removeEventListener === 'function') {
                document.removeEventListener('contextmenu', this._adminGridContextMenuHandler, true);
                this._adminGridContextMenuHandler = null;
            }
        }

        /** Confirm and trash a file from the grid context menu. */
        private confirmAndTrashFile(fileId: number): void {
            const title = vmFileSettings?.strings?.moveToTrash || 'Move to Trash';
            const msg = vmFileSettings?.strings?.confirmTrash || 'Are you sure you want to move this file to trash?';

            if (!confirm(msg)) return;

            const ajaxUrl = vmFileSettings?.ajaxUrl || '/wp-admin/admin-ajax.php';
            const nonce = vmFileSettings?.nonce || '';

            const trashPayload: Record<string, any> = {
                action: 'vm_drive_trash_file',
                file_id: fileId,
                nonce: nonce,
                drive_type: this.getCurrentDriveType()
            };
            const urlCuid = new URLSearchParams(window.location.search).get('custom_user_id')
                || new URLSearchParams(window.location.search).get('user_id');
            if (urlCuid) trashPayload.custom_user_id = urlCuid;
            $.post(ajaxUrl, trashPayload)
            .done((response: any) => {
                if (response.success) {
                    try {
                        if ((window as any).wp?.media?.frame) {
                            (window as any).wp.media.frame.content.get().collection.props.set({ ignore: (+new Date()) });
                        } else {
                            window.location.reload();
                            return;
                        }
                    } catch (_) {}
                    try {
                        jQuery(document).trigger('rml:media:refresh');
                    } catch (_) {}
                } else {
                    alert(response.data?.message || 'Failed to trash file');
                }
            })
            .fail(() => {
                alert('Network error occurred');
            });
        }

        private getTrashNonce(): string {
            const tabs = (window as any).mediaLibraryTabs;
            if (tabs?.trashNonce) {
                return tabs.trashNonce;
            }
            return vmFileSettings?.nonce || '';
        }

        /** Confirm and restore a file from trash grid context menu. */
        private confirmAndRestoreFile(fileId: number): void {
            const msg = 'Restore this file?';
            if (!confirm(msg)) {
                return;
            }

            const ajaxUrl = vmFileSettings?.ajaxUrl || '/wp-admin/admin-ajax.php';
            $.post(ajaxUrl, {
                action: 'vm_drive_restore_file',
                file_id: fileId,
                nonce: this.getTrashNonce(),
            }).done((response: any) => {
                if (response.success) {
                    try {
                        if ((window as any).wp?.media?.frame) {
                            (window as any).wp.media.frame.content.get().collection.props.set({ ignore: (+new Date()) });
                        } else {
                            window.location.reload();
                        }
                    } catch (_) {}
                    try {
                        jQuery(document).trigger('rml:media:refresh');
                    } catch (_) {}
                } else {
                    alert(response.data?.message || 'Failed to restore file');
                }
            }).fail(() => {
                alert('Network error occurred');
            });
        }

        /** Confirm and permanently delete a file from trash grid context menu. */
        private confirmAndDeletePermanently(fileId: number): void {
            const msg = 'Delete this file permanently? This cannot be undone.';
            if (!confirm(msg)) {
                return;
            }

            const ajaxUrl = vmFileSettings?.ajaxUrl || '/wp-admin/admin-ajax.php';
            $.post(ajaxUrl, {
                action: 'vm_drive_delete_permanently',
                file_id: fileId,
                nonce: this.getTrashNonce(),
            }).done((response: any) => {
                if (response.success) {
                    try {
                        if ((window as any).wp?.media?.frame) {
                            (window as any).wp.media.frame.content.get().collection.props.set({ ignore: (+new Date()) });
                        } else {
                            window.location.reload();
                        }
                    } catch (_) {}
                    try {
                        jQuery(document).trigger('rml:media:refresh');
                    } catch (_) {}
                } else {
                    alert(response.data?.message || 'Failed to delete file');
                }
            }).fail(() => {
                alert('Network error occurred');
            });
        }

        private hideSearchResults(context?: string): void {
            if (context) {
                $(`.vm-search-results[data-context="${context}"]`).hide().empty();
            } else {
                $('.vm-search-results').hide().empty();
            }
        }

        private toggleSearchContainer(context: string): void {
            const $container = $(`.vm-search-container[data-context="${context}"]`);
            const $input = $container.find('.vm-user-search-input');
            
            if ($container.is(':visible')) {
                $container.hide();
                $input.val('');
                this.hideSearchResults(context);
            } else {
                // Hide other search containers first
                $('.vm-search-container').hide();
                $('.vm-user-search-input').val('');
                this.hideSearchResults();
                
                // Show this container and focus input
                $container.show();
                $input.focus();
            }
        }

        private clearSearch(context?: string): void {
            if (context) {
                $(`.vm-user-search-input[data-context="${context}"]`).val('');
                this.hideSearchResults(context);
            } else {
                $('.vm-user-search-input').val('');
                this.hideSearchResults();
            }
        }

        private addEntityToAccess(entityData: any, context: string): void {
            if (!this.currentSettings) return;

            // Check if entity already exists across ALL contexts to prevent duplicates
            const entityKey = `${entityData.type}:${entityData.id}`;
            const allEntities = [
                ...this.currentSettings.entities,
                ...this.currentSettings.general_access_entities,
                ...this.currentSettings.exceptions
            ];
            
            const existingEntity = allEntities.find(e => `${e.type}:${e.id}` === entityKey);
            if (existingEntity) {
                return; // Entity already exists in any context
            }

            const newEntity: DriveEntity = {
                id: entityData.id,
                name: entityData.name,
                email: entityData.email,
                type: entityData.type,
                role: 'viewer',
                can_remove: true,
                can_edit: true,
                avatar: entityData.avatar,
                description: entityData.description,
                badge: entityData.badge,
                member_count: entityData.member_count
            };

            // Add to appropriate array
            switch (context) {
                case 'entities':
                    this.currentSettings.entities.push(newEntity);
                    break;
                case 'general-access':
                    // For org_drive, route general-access users to exceptions
                    if (this.currentSettings.drive_type === 'org_drive') {
                        this.currentSettings.exceptions.push(newEntity);
                    } else {
                        this.currentSettings.general_access_entities.push(newEntity);
                    }
                    break;
                case 'exceptions':
                    this.currentSettings.exceptions.push(newEntity);
                    break;
                case 'folder-owner':
                    // Cannot add to folder owner context, it's set automatically
                    return;
            }

            this.renderSettings(); // Re-render
            
            // If we routed general-access to exceptions for org_drive, hide the search container
            if (context === 'general-access' && this.currentSettings.drive_type === 'org_drive') {
                this.toggleSearchContainer('general-access');
            }
        }

        private addProfileOwnerToExceptions(entityId: string, context: string): void {
            // Not used for file settings - account holder is specific to folders
            return;
        }

        public open(fileId: number, driveType?: string): void {
            // Guard: if wrong arguments passed (folder manage access routed here), reroute to folder modal
            const looksLikeDrive = ((): boolean => {
                const anyVal: any = fileId as any;
                return typeof anyVal === 'string' && ['media_gallery', 'user_files', 'my_files', 'org_drive'].indexOf(anyVal) !== -1;
            })();
            const looksLikeFolderId = ((): boolean => {
                const anyDrive: any = driveType as any;
                return (typeof anyDrive === 'number' && !Number.isNaN(anyDrive)) || (typeof anyDrive === 'string' && /^\d+$/.test(anyDrive));
            })();
            if (looksLikeDrive && looksLikeFolderId && typeof (window as any).driveSettingsModal !== 'undefined') {
                const folderId = typeof driveType === 'string' ? parseInt(driveType as any, 10) : ((driveType as any) as number);
                (window as any).driveSettingsModal.open((fileId as any) as 'media_gallery' | 'user_files' | 'my_files' | 'org_drive', folderId);
                return;
            }

            // Prevent double-opening
            if (this.isOpen) {
                return;
            }
            
            this.isOpen = true;

            // Initialize modal with title and empty content
            this.modal.clean_modal();
            this.modal.init_modal('Share File', '', '');
            this.modal.showModal();
            // E-03: move keyboard focus into the modal so screen readers announce it and Tab works.
            setTimeout(() => {
                const modalEl = document.getElementById('vm-file-settings-modal');
                if (modalEl) {
                    if (!modalEl.hasAttribute('tabindex')) modalEl.setAttribute('tabindex', '-1');
                    modalEl.focus();
                }
            }, 0);
            this.loadSettings(fileId, driveType);
        }

        public close(): void {
            this.isOpen = false;
            this.modal.closeModal();
            this.currentSettings = null;
        }

        private loadSettings(fileId: number, driveType?: string): void {
            this.showLoading();

            const ajaxUrl = vmFileSettings?.ajaxUrl || '/wp-admin/admin-ajax.php';
            const nonce = vmFileSettings?.nonce || '';

            $.post(ajaxUrl, {
                action: 'get_file_settings',
                file_id: fileId,
                drive: driveType || '',
                nonce: nonce
            })
            .done((response: any) => {
                if (response.success) {
                    // Backend returns response.data.settings
                    this.currentSettings = response.data.settings || response.data;
                    
                    // Ensure arrays exist
                    if (!this.currentSettings.entities) {
                        this.currentSettings.entities = [];
                    }
                    if (!this.currentSettings.general_access_entities) {
                        this.currentSettings.general_access_entities = [];
                    }
                    if (!this.currentSettings.exceptions) {
                        this.currentSettings.exceptions = [];
                    }
                    this.renderSettings();
                } else {
                    // If backend signals this was a folder settings request, reroute to folder modal
                    const data = response?.data || {};
                    if (
                        data?.redirect_to === 'drive_settings' &&
                        typeof (window as any).driveSettingsModal !== 'undefined' &&
                        data?.drive_type &&
                        (data?.folder_id !== undefined && data?.folder_id !== null)
                    ) {
                        const folderId = parseInt(String(data.folder_id), 10);
                        if (!Number.isNaN(folderId)) {
                            (window as any).driveSettingsModal.open(data.drive_type, folderId);
                            return;
                        }
                    }
                    this.showError(response.data?.message || response.data || 'Failed to load settings');
                }
            })
            .fail(() => {
                this.showError('Network error occurred');
            });
        }

        private renderSettings(): void {
            if (!this.currentSettings) return;

            const settings = this.currentSettings;
            let html = `<div class="vm-drive-settings">`;

         

            // Main entities section (title from backend for i18n)
            let sectionTitle = this.getString('systemRolesWithAccess', 'System Roles with Access');
            let showMainSearch = true;
            
            if (settings.drive_type === 'user_files') {
                showMainSearch = true;
            } else if (settings.drive_type === 'org_drive') {
                sectionTitle = this.getString('systemRolesWithAccess', 'System Roles with Access');
            } else if (settings.drive_type === 'media_gallery') {
                showMainSearch = false; // Media Library: no permissions can be changed
            } else {
                // For files without drive_type, show search
                showMainSearch = true;
            }
                
            html += `
                <div class="vm-settings-section">
                    <div class="vm-section-header">
                        <h4>${sectionTitle}</h4>
                    </div>
                    ${showMainSearch ? `
                        <div class="vm-user-search-container">
                            <div class="vm-search-input-wrapper">
                                <svg class="vm-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
                                </svg>
                                <input type="text" 
                                       class="vm-user-search-input" 
                                       placeholder="${this.getString('addSystemRole', 'Add System Role')}"
                                       data-context="entities">
                            </div>
                            <div class="vm-search-results" data-context="entities" style="display: none;"></div>
                        </div>
                    ` : ''}
                    <div class="vm-users-list" data-context="entities">
                        ${settings.file_owner ? `
                            ${this.renderEntityItem(settings.file_owner, 'file-owner')}
                        ` : ''}
                        ${settings.entities.length > 0 ? `
                            ${this.renderEntitiesList(settings.entities, 'entities')}
                        ` : ''}
                        ${!showMainSearch && settings.drive_type === 'media_gallery' ? `
                            <p class="vm-no-permissions">Media Library access is controlled by user permissions</p>
                        ` : ''}
                    </div>
                </div>
            `;

            // General Access section
            const isGeneralAccessDisabled = settings.drive_type === 'media_gallery' || (settings.general_access_readonly || false);
            
            html += `
                <div class="vm-settings-section">
                    <div class="vm-section-header">
                        <h4>General Access (Front-end)</h4>
                    </div>
                    <div class="vm-general-access-box ${isGeneralAccessDisabled ? 'vm-general-access-disabled' : ''}">
                        <div class="vm-access-icon">
                            <span class="dashicons ${settings.general_access === 'restricted' ? 'dashicons-lock' : 'dashicons-admin-site-alt3'}"></span>
                        </div>
                        <div class="vm-access-content">
                            <div style="display: flex; align-items: center; gap: 10px;">
                                <select id="vm-general-access-select" ${isGeneralAccessDisabled ? 'disabled' : ''}>
                                    ${this.buildGeneralAccessOptions(settings)}
                                </select>
                                ${settings.drive_type === 'org_drive' && ['anyone_with_link', 'logged_in_users', 'anyone'].indexOf(settings.general_access) !== -1 ? `
                                    <button type="button" id="vm-copy-file-link-btn" class="button button-small" style="padding: 4px 8px; min-width: auto; width: 28px; height: 28px; display: flex; align-items: center; justify-content: center;" title="Copy link">
                                        <span class="dashicons dashicons-admin-links" style="font-size: 16px; width: 16px; height: 16px; line-height: 16px; margin: 0;"></span>
                                    </button>
                                ` : ''}
                            </div>
                            <p class="vm-access-description" id="vm-access-description">
                                ${this.getAccessDescription(settings.general_access, settings.drive_type)}
                            </p>
                        </div>
                    </div>
                    ${settings.general_access_entities.length > 0 ? `
                        <div class="vm-users-list" data-context="general-access">
                            ${this.renderEntitiesList(settings.general_access_entities, 'general-access')}
                        </div>
                    ` : ''}
                </div>
            `;

            // Exceptions section - Show for all drive types except media_gallery
            if (settings.drive_type !== 'media_gallery') {
                html += `
                    <div class="vm-settings-section">
                        <div class="vm-section-header">
                            <h4>Exceptions</h4>
                            <button type="button" class="vm-add-people-btn vm-btn-small" data-context="exceptions">+</button>
                        </div>
                        <div class="vm-user-search-container vm-search-container" data-context="exceptions" style="display: none;">
                            <div class="vm-search-input-wrapper">
                                <svg class="vm-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
                                </svg>
                                <input type="text" 
                                       class="vm-user-search-input" 
                                       placeholder="${this.getString('addPeople', 'Add People')}"
                                       data-context="exceptions">
                            </div>
                            <div class="vm-search-results" data-context="exceptions" style="display: none;"></div>
                        </div>
                        <div class="vm-users-list" data-context="exceptions">
                            ${settings.exceptions && settings.exceptions.length > 0 ? 
                                this.renderEntitiesList(settings.exceptions, 'exceptions') : 
                                '<p class="vm-no-users">No exceptions added</p>'
                            }
                        </div>
                    </div>
                `;
            }

            // Update WC modal content and footer
            this.modal.init_modal(settings.file_name, html, this.getModalFooter());
            
            // Toggle copy link button based on current general access
            if (this.currentSettings) {
                this.toggleCopyLinkButton(this.currentSettings.general_access);
            }
        }

        private renderEntitiesList(entities: DriveEntity[], context: string): string {
            if (!entities.length) {
                return '<p class="vm-no-users">No entities added</p>';
            }

            return entities.map(entity => this.renderEntityItem(entity, context)).join('');
        }

        private renderEntityItem(entity: DriveEntity, context: string): string {
            const icon = this.getEntityIcon(entity.type);
            const badge = entity.badge || this.getEntityBadge(entity.type);
            const isVmAdmin = entity.type === 'system_role' && entity.id === 'vm-admin';
            const isProfileOwner = context === 'profile-owner';
            const isProfileOwnerInExceptions = entity.type === 'profile_owner' && context === 'exceptions';
            const isDisabled = isVmAdmin || !entity.can_edit;
            
            
            return `
                <div class="vm-user-item ${isDisabled ? 'vm-user-disabled' : ''} ${isProfileOwner ? 'vm-profile-owner' : ''}" data-user-id="${entity.id}" data-entity-type="${entity.type}">
                    <div class="vm-user-avatar vm-entity-${entity.type}">
                        ${entity.avatar ? `<img src="${entity.avatar}" alt="${entity.name}">` : icon}
                    </div>
                    <div class="vm-user-info">
                        <div class="vm-user-name">
                            ${entity.name}
                            ${badge ? `<span class="vm-entity-badge vm-badge-${entity.type}">${badge}</span>` : ''}
                            ${isProfileOwner ? `<span class="vm-entity-badge vm-badge-profile-owner">Account Holder</span>` : ''}
                        </div>
                        <div class="vm-user-email">
                            ${entity.email || entity.description || this.getEntityDescription(entity)}
                        </div>
                    </div>
                    <div class="vm-user-role">
                        ${this.renderRoleSelect(entity, context, isDisabled, isVmAdmin)}
                    </div>
                    ${entity.can_remove && !isVmAdmin && (!isProfileOwner || isProfileOwnerInExceptions) ? `
                        <button type="button" class="vm-remove-user" data-user-id="${entity.id}" data-context="${context}">×</button>
                    ` : ''}
                    ${isProfileOwner ? `
                        <div class="vm-profile-owner-actions">
                            <button type="button" class="vm-add-to-exceptions-btn" data-user-id="${entity.id}" data-context="profile-owner">
                                Add to Exceptions
                            </button>
                        </div>
                    ` : ''}
                </div>
            `;
        }

        private renderRoleSelect(entity: DriveEntity, context: string, isDisabled: boolean, isVmAdmin: boolean): string {
            const settings = this.currentSettings;
            if (!settings) return '';

            // Determine available roles - always show viewer, contributor, editor for all drives
            let availableRoles = ['viewer', 'contributor', 'editor'];
            
            if (isVmAdmin) {
                return `<select class="vm-role-select" data-user-id="${entity.id}" disabled><option value="admin" selected>Admin</option></select>`;
            }

            let roleSelect = `<select class="vm-role-select" data-user-id="${entity.id}" ${isDisabled ? 'disabled' : ''}>`;
            
            availableRoles.forEach(role => {
                const selected = entity.role === role ? 'selected' : '';
                const roleLabel = this.getString(role, role.charAt(0).toUpperCase() + role.slice(1));
                roleSelect += `<option value="${role}" ${selected}>${roleLabel}</option>`;
            });
            
            roleSelect += `</select>`;
            
            return roleSelect;
        }

        private getEntityIcon(type: string): string {
            switch (type) {
                case 'user': return '<span class="dashicons dashicons-admin-users"></span>';
                case 'system_role': return '<span class="dashicons dashicons-admin-settings"></span>';
                case 'taxonomy_group': return '<span class="dashicons dashicons-category"></span>';
                case 'secondary_role': return '<span class="dashicons dashicons-admin-tools"></span>';
                case 'member_role': return '<span class="dashicons dashicons-id"></span>';
                case 'profile_owner': return '<span class="dashicons dashicons-admin-users"></span>';
                default: return '<span class="dashicons dashicons-admin-users"></span>';
            }
        }

        private getEntityBadge(type: string): string {
            switch (type) {
                case 'system_role': return 'Role';
                case 'taxonomy_group': return 'Group';
                case 'secondary_role': return 'Secondary';
                case 'member_role': return 'Member';
                case 'profile_owner': return 'Account Holder';
                default: return '';
            }
        }

        private getEntityDescription(entity: DriveEntity): string {
            if (entity.member_count && entity.member_count > 0) {
                return `${entity.member_count} members`;
            }
            
            switch (entity.type) {
                case 'system_role': return 'System role';
                case 'taxonomy_group': return 'Contact group';
                case 'secondary_role': return 'Secondary role';
                case 'member_role': return 'Membership role';
                case 'profile_owner': return 'Account Holder';
                default: return '';
            }
        }

        private buildGeneralAccessOptions(settings: FileSettings): string {
            const opts = settings.general_access_options;
            if (opts) {
                const legacyMap = ['logged_in_users', 'anyone', 'active_members'];
                return Object.keys(opts).map((key) => {
                    const isSelected = key === settings.general_access
                        || (key === 'anyone_with_link' && legacyMap.indexOf(settings.general_access) !== -1);
                    return `<option value="${key}" ${isSelected ? 'selected' : ''}>${opts[key]}</option>`;
                }).join('');
            }
            return `<option value="restricted" ${settings.general_access === 'restricted' ? 'selected' : ''}>${this.getString('restricted', 'Restricted')}</option>`;
        }

        private getAccessDescription(access: string, driveType: string): string {
            const descriptions = this.currentSettings?.general_access_descriptions;
            if (descriptions) {
                const key = (['logged_in_users', 'anyone', 'active_members'].indexOf(access) !== -1)
                    ? 'anyone_with_link'
                    : access;
                if (descriptions[key]) {
                    return descriptions[key];
                }
            }

            return this.currentSettings?.general_access_description || '';
        }

        private updateGeneralAccessDescription(access: string): void {
            const description = this.getAccessDescription(access, this.currentSettings?.drive_type || 'org_drive');
            $('#vm-access-description').text(description);
            
            // Update icon
            const icon = access === 'restricted' ? '<span class="dashicons dashicons-lock"></span>' : '<span class="dashicons dashicons-admin-site-alt3"></span>';
            $('.vm-access-icon').html(icon);
        }

        private toggleCopyLinkButton(access: string): void {
            const $copyBtn = $('#vm-copy-file-link-btn');
            if (['anyone_with_link', 'logged_in_users', 'anyone'].indexOf(access) !== -1 && this.currentSettings?.drive_type === 'org_drive') {
                if ($copyBtn.length === 0) {
                    // Button doesn't exist, add it
                    const $select = $('#vm-general-access-select');
                    $select.after(`
                        <button type="button" id="vm-copy-file-link-btn" class="button button-small" style="padding: 4px 8px; min-width: auto; width: 28px; height: 28px; display: flex; align-items: center; justify-content: center; margin-left: 10px;" title="Copy link">
                            <span class="dashicons dashicons-admin-links" style="font-size: 16px; width: 16px; height: 16px; line-height: 16px; margin: 0;"></span>
                        </button>
                    `);
                } else {
                    $copyBtn.show();
                }
            } else {
                $copyBtn.hide();
            }
        }

        private copyFileLink(): void {
            if (!this.currentSettings) {
                return;
            }

            const fileId = this.currentSettings.file_id;
            const ajaxUrl = vmFileSettings?.ajaxUrl || '/wp-admin/admin-ajax.php';
            const nonce = vmFileSettings?.driveSettingsNonce || vmFileSettings?.nonce || '';

            // Get file URL
            $.post(ajaxUrl, {
                action: 'get_attachment_url',
                attachment_id: fileId,
                nonce: nonce
            })
            .done((response: any) => {
                if (response.success && response.data && response.data.url) {
                    const fileUrl = response.data.url;
                    
                    // Copy to clipboard
                    if (navigator.clipboard && navigator.clipboard.writeText) {
                        navigator.clipboard.writeText(fileUrl).then(() => {
                            const $btn = $('#vm-copy-file-link-btn');
                            const originalHtml = $btn.html();
                            $btn.attr('title', 'Copied!');
                            $btn.html('<span class="dashicons dashicons-yes" style="font-size: 16px; width: 16px; height: 16px; line-height: 16px; margin: 0;"></span>');
                            setTimeout(() => {
                                $btn.html(originalHtml);
                                $btn.attr('title', 'Copy link');
                            }, 2000);
                        }).catch(() => {
                            // Fallback: select text in a temporary input
                            this.fallbackCopyTextToClipboard(fileUrl);
                        });
                    } else {
                        // Fallback for older browsers
                        this.fallbackCopyTextToClipboard(fileUrl);
                    }
                } else {
                    $('#vm-file-link-error').text('Failed to generate file link.').show();
                }
            })
            .fail(() => {
                $('#vm-file-link-error').text('Failed to generate file link.').show();
            });
        }

        private fallbackCopyTextToClipboard(text: string): void {
            const textArea = document.createElement('textarea');
            textArea.value = text;
            textArea.style.position = 'fixed';
            textArea.style.left = '-999999px';
            textArea.style.top = '-999999px';
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();
            try {
                document.execCommand('copy');
                const $btn = $('#vm-copy-file-link-btn');
                const originalHtml = $btn.html();
                $btn.attr('title', 'Copied!');
                $btn.html('<span class="dashicons dashicons-yes" style="font-size: 16px; width: 16px; height: 16px; line-height: 16px; margin: 0;"></span>');
                setTimeout(() => {
                    $btn.html(originalHtml);
                    $btn.attr('title', 'Copy link');
                }, 2000);
            } catch (err) {
                const $linkSection = $('#vm-copy-file-link-btn').closest('div');
                let $fallback = $linkSection.find('.vm-copy-fallback-input');
                if ($fallback.length === 0) {
                    $fallback = $('<input type="text" class="vm-copy-fallback-input" readonly style="width:100%; margin-top:6px; font-size:12px; padding:4px;" />');
                    $linkSection.append($fallback);
                }
                $fallback.val(text);
                ($fallback.get(0) as HTMLInputElement).select();
            }
            document.body.removeChild(textArea);
        }

        private searchUsers(searchTerm: string, context: string): void {
            const ajaxUrl = vmFileSettings?.ajaxUrl || '/wp-admin/admin-ajax.php';
            // Use driveSettingsNonce for shared drive endpoints
            const driveNonce = vmFileSettings?.driveSettingsNonce || '';

            // Show loading in search results
            const $searchResults = $(`.vm-search-results[data-context="${context}"]`);
            $searchResults.html('<div class="vm-search-loading">Searching...</div>').show();

            const searchData = {
                action: 'search_drive_users',
                search_term: searchTerm,
                drive_type: this.currentSettings?.drive_type,
                search_context: context,
                file_id: this.currentSettings?.file_id || 0,
                nonce: driveNonce
            };
            
            
            $.post(ajaxUrl, searchData)
            .done((response: any) => {
                
                if (response.success && response.data && response.data.length > 0) {
                    this.displaySearchResults(response.data, context);
                } else {
                    $searchResults.html('<div class="vm-search-no-results">No users found</div>');
                }
            })
            .fail(() => {
                $searchResults.html('<div class="vm-search-error">Search failed. Please try again.</div>');
            });
        }

        private displaySearchResults(entities: any[], context: string): void {
            const $searchResults = $(`.vm-search-results[data-context="${context}"]`);
            
            if (entities.length === 0) {
                $searchResults.html('<div class="vm-search-no-results">No results found</div>').show();
                return;
            }

            // Group entities by type
            const groupedEntities = this.groupEntitiesByType(entities);
            let html = '';

            // For user_files drive and exceptions context, show Account Holder first if available
            if ((this.currentSettings?.drive_type === 'user_files' || this.currentSettings?.drive_type === 'my_files') && context === 'exceptions' && this.currentSettings.file_owner) {
                const profileOwner = this.currentSettings.file_owner;
                if (profileOwner) {
                    // Check if Account Holder is already in the search results
                    const profileOwnerInResults = entities.some(entity => entity.type === 'profile_owner' && entity.id === profileOwner.id);
                    
                    if (!profileOwnerInResults) {
                        html += `
                            <div class="vm-search-group vm-profile-owner-group">
                                <div class="vm-search-group-header">
                                    <span class="dashicons dashicons-admin-users"></span>
                                    <span class="vm-search-group-title">Account Holder</span>
                                    <span class="vm-search-group-count">(1)</span>
                                </div>
                                <div class="vm-search-group-items">
                                    <div class="vm-search-result-item vm-entity-profile-owner vm-profile-owner-highlight" 
                                         data-entity-id="${profileOwner.id}"
                                         data-entity-name="${profileOwner.name}"
                                         data-entity-email="${profileOwner.email || ''}"
                                         data-entity-type="profile_owner"
                                         data-entity-avatar="${profileOwner.avatar || ''}"
                                         data-entity-description="${profileOwner.description || ''}"
                                         data-entity-badge="Account Holder"
                                         data-member-count="0"
                                         data-context="${context}">
                                        <div class="vm-user-avatar vm-entity-profile_owner">
                                            ${profileOwner.avatar ? `<img src="${profileOwner.avatar}" alt="${profileOwner.name}">` : '<span class="dashicons dashicons-admin-users"></span>'}
                                        </div>
                                        <div class="vm-user-details">
                                            <div class="vm-user-name">
                                                ${profileOwner.name}
                                                <span class="vm-entity-badge vm-badge-profile-owner">Account Holder</span>
                                            </div>
                                            <div class="vm-user-email">${profileOwner.email || profileOwner.description || 'Account Holder'}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        `;
                    }
                }
            }

            // Render each group
            Object.keys(groupedEntities).forEach(type => {
                const typeEntities = groupedEntities[type];
                const typeLabel = this.getEntityTypeLabel(type);
                const typeIcon = this.getEntityIcon(type);
                
                html += `
                    <div class="vm-search-group">
                        <div class="vm-search-group-header">
                            ${typeIcon}
                            <span class="vm-search-group-title">${typeLabel}</span>
                            <span class="vm-search-group-count">(${typeEntities.length})</span>
                        </div>
                        <div class="vm-search-group-items">
                `;
                
                typeEntities.forEach(entity => {
                    const badge = entity.badge || this.getEntityBadge(entity.type);
                    const description = entity.email || entity.description || this.getEntityDescriptionFromData(entity);
                    
                    html += `
                        <div class="vm-search-result-item vm-entity-${entity.type}" 
                             data-entity-id="${entity.id}"
                             data-entity-name="${entity.name}"
                             data-entity-email="${entity.email || ''}"
                             data-entity-type="${entity.type}"
                             data-entity-avatar="${entity.avatar || ''}"
                             data-entity-description="${entity.description || ''}"
                             data-entity-badge="${entity.badge || ''}"
                             data-member-count="${entity.member_count || 0}"
                             data-context="${context}">
                            <div class="vm-user-avatar vm-entity-${entity.type}">
                                ${entity.avatar ? `<img src="${entity.avatar}" alt="${entity.name}">` : this.getEntityIcon(entity.type)}
                            </div>
                            <div class="vm-user-details">
                                <div class="vm-user-name">
                                    ${entity.name}
                                    ${badge ? `<span class="vm-entity-badge vm-badge-${entity.type}">${badge}</span>` : ''}
                                </div>
                                <div class="vm-user-email">${description}</div>
                            </div>
                        </div>
                    `;
                });
                
                html += `
                        </div>
                    </div>
                `;
            });

            $searchResults.html(html).show();
        }

        private groupEntitiesByType(entities: any[]): { [key: string]: any[] } {
            const groups: { [key: string]: any[] } = {};
            
            entities.forEach(entity => {
                if (!groups[entity.type]) {
                    groups[entity.type] = [];
                }
                groups[entity.type].push(entity);
            });
            
            return groups;
        }

        private getEntityTypeLabel(type: string): string {
            switch (type) {
                case 'user': return 'Users';
                case 'system_role': return 'System Roles';
                case 'taxonomy_group': return 'Groups';
                case 'secondary_role': return 'Secondary Roles';
                case 'member_role': return 'Member Roles';
                case 'profile_owner': return 'Account Holder';
                default: return 'Other';
            }
        }

        private getEntityDescriptionFromData(entity: any): string {
            if (entity.member_count && entity.member_count > 0) {
                return `${entity.member_count} members`;
            }
            
            switch (entity.type) {
                case 'system_role': return 'System role';
                case 'taxonomy_group': return 'Contact group';
                case 'secondary_role': return 'Secondary role';
                case 'member_role': return 'Membership role';
                case 'profile_owner': return 'Account Holder';
                default: return entity.type || '';
            }
        }

        private removeEntity(entityId: string, context: string): void {
            if (!this.currentSettings) {
                return;
            }

            // Remove from appropriate array
            switch (context) {
                case 'entities':
                    this.currentSettings.entities = this.currentSettings.entities.filter(e => String(e.id) !== String(entityId));
                    break;
                case 'general-access':
                    this.currentSettings.general_access_entities = this.currentSettings.general_access_entities.filter(e => String(e.id) !== String(entityId));
                    break;
                case 'exceptions':
                    this.currentSettings.exceptions = this.currentSettings.exceptions.filter(e => String(e.id) !== String(entityId));
                    break;
                case 'folder-owner':
                    // Folder owner cannot be removed, but handle gracefully
                    return;
                default:
                    return;
            }

            this.renderSettings(); // Re-render
        }

        private async saveSettings(): Promise<void> {
            if (!this.currentSettings) return;

            // Collect current form data
            const generalAccess = $('#vm-general-access-select').val();
            this.currentSettings.general_access = generalAccess as FileSettings['general_access'];

            // Collect role changes
            $('.vm-role-select').each((_: any, select: any) => {
                const $select = $(select);
                const entityId = $select.data('user-id');
                const newRole = $select.val();
                
                // Update role in all entity arrays (normalize ID types for comparison)
                [this.currentSettings!.entities, this.currentSettings!.general_access_entities, this.currentSettings!.exceptions].forEach(entityArray => {
                    const entity = entityArray.find(e => String(e.id) === String(entityId));
                    if (entity) {
                        entity.role = newRole as any;
                    }
                });
            });

			// Preflight: gate with "Apply to Entire Tree" before saving
			const saveBtnEl = (window as any).jQuery('#vm-file-settings-modal #vm-drive-save-btn').get(0);
			if (saveBtnEl) {
				this.spinnerController.toggleSpinnerOnButton(saveBtnEl, true);
                jQuery('#vm-file-settings-modal #vm-drive-save-btn').addClass('disabled');
			}
            try {
                const parentId = await this.fetchFileParentFolderId(this.currentSettings.file_id);
                if (parentId && parentId > 0) {
                    const applyMode = await this.showFilePermissionPromptModal(parentId);
					if (applyMode === null) {
						// User cancelled
						if (saveBtnEl) {
							this.spinnerController.toggleSpinnerOnButton(saveBtnEl, false);
                            jQuery('#vm-file-settings-modal #vm-drive-save-btn').removeClass('disabled');
						}
						return; // Cancel → abort save entirely
					}
                    // Store the apply mode for backend
                    (this.currentSettings as any).apply_mode = applyMode; // 'file_only' or 'folder_tree'
                } else {
                    // No parent folder, apply to file only
                    (this.currentSettings as any).apply_mode = 'file_only';
                }
            } catch (e) {
                (this.currentSettings as any).apply_mode = 'file_only';
            }

            // Save via AJAX — send settings as JSON so PHP receives full structure (avoids form serialization truncation)
            const ajaxUrl = vmFileSettings?.ajaxUrl || '/wp-admin/admin-ajax.php';
            const nonce = vmFileSettings?.nonce || '';

            $.post(ajaxUrl, {
                action: 'save_file_settings',
                file_id: this.currentSettings.file_id,
                settings_json: JSON.stringify(this.currentSettings),
                drive_type: this.currentSettings.drive_type || this.getCurrentDriveType(),
                nonce: nonce
            })
            .done((response: any) => {
                if (response.success) {
                    // Show impact information if child folders/files are affected
                    if (response.data.impact && response.data.impact.total_affected > 0) {
                        this.showSuccess(`${response.data.message} This change affects ${response.data.impact.child_folders} child folders and ${response.data.impact.child_files} files.`);
                        setTimeout(() => this.close(), 3000); // Close after showing message
                    } else {
                        this.showSuccess(response.data?.message || 'File permissions saved.');
                        setTimeout(() => this.close(), 1500);
                    }

                    // Trigger refresh of breadcrumb or folder view
                    $(document).trigger('drive-settings-saved', this.currentSettings);
                } else {
                    // Handle hierarchical validation errors
                    if (response.data && response.data.type === 'hierarchy_validation') {
                        this.showHierarchyError(response.data.message, response.data.restricted_parents, response.data);
                    } else {
                        this.showError(response.data?.message || response.data || 'Failed to save settings');
                    }
                }
            })
            .fail(() => {
                this.showError('Network error occurred');
			})
			.always(() => {
				if (saveBtnEl) {
					this.spinnerController.toggleSpinnerOnButton(saveBtnEl, false);
                    jQuery('#vm-file-settings-modal #vm-drive-save-btn').removeClass('disabled');
				}
				const applyBtn = (window as any).jQuery('#vm-propagate-apply').get(0);
				if (applyBtn) {
					this.spinnerController.toggleSpinnerOnButton(applyBtn, false);
                    jQuery('#vm-propagate-apply').removeClass('disabled');
				}
            });
        }

        private async fetchFileParentFolderId(fileId: number): Promise<number> {
            const ajaxUrl = vmFileSettings?.ajaxUrl || '/wp-admin/admin-ajax.php';
            const nonce = vmFileSettings?.driveSettingsNonce || vmFileSettings?.nonce || '';
            const hintFolderId = this.getHintFolderId();
            return new Promise<number>((resolve) => {
                (window as any).jQuery.post(ajaxUrl, {
                    action: 'get_parent_folder',
                    file_id: fileId,
                    folder_id: hintFolderId || 0,
                    nonce
                }).done((resp: any) => {
                    resolve(resp?.success ? (parseInt(String(resp.data?.parent_id), 10) || 0) : 0);
                }).fail(() => resolve(0));
            });
        }

        private getHintFolderId(): number | null {
            try {
                if (typeof (window as any).getRMLCurrentFolder === 'function') {
                    const id = (window as any).getRMLCurrentFolder();
                    if (id && id > 0) return id;
                }
            } catch {}
            try {
                const el = document.querySelector('.vm-breadcrumb-current');
                const attr = el ? (el as HTMLElement).getAttribute('data-folder-id') : null;
                if (attr && attr !== '0') return parseInt(attr, 10);
            } catch {}
            return null;
        }

        private async fetchPropagationImpact(folderId: number, driveType?: string, fileId?: number): Promise<{child_folders:number, child_files:number, total_affected:number}> {
            const ajaxUrl = vmFileSettings?.ajaxUrl || '/wp-admin/admin-ajax.php';
            const nonce = vmFileSettings?.driveSettingsNonce || vmFileSettings?.nonce || '';
            return new Promise((resolve) => {
                (window as any).jQuery.post(ajaxUrl, {
                    action: 'get_propagation_impact',
                    folder_id: folderId,
                    drive_type: driveType || '',
                    file_id: fileId || 0,
                    include_parent: 1,
                    nonce
                }).done((resp: any) => {
                    if (resp?.success) {
                        resolve(resp.data);
                    } else {
                        resolve({ child_folders: 0, child_files: 0, total_affected: 0 });
                    }
                }).fail(() => resolve({ child_folders: 0, child_files: 0, total_affected: 0 }));
            });
        }

		private async showFilePermissionPromptModal(parentFolderId: number): Promise<'file_only' | 'folder_tree' | null> {
            const driveType = this.currentSettings?.drive_type || this.getCurrentDriveType();
            const impact = await this.fetchPropagationImpact(parentFolderId, driveType, this.currentSettings?.file_id);
            const title = 'Apply Permissions';
            const body = `
                <div class="vm-propagation-prompt">
                    <p>This file belongs to a folder. How would you like to apply these permissions?</p>
                    <div style="margin: 20px 0; padding: 15px; background: #f0f0f1; border-left: 4px solid #2271b1;">
                        <p style="margin: 0 0 10px 0;"><strong>Apply to file only:</strong></p>
                        <p style="margin: 0; font-size: 13px;">This file will have independent permissions. It will appear in "Shared with Me" drive even if the parent folder is not shared.</p>
                    </div>
                    <div style="margin: 20px 0; padding: 15px; background: #f0f0f1; border-left: 4px solid #2271b1;">
                        <p style="margin: 0 0 10px 0;"><strong>Apply to folder tree:</strong></p>
                        <p style="margin: 0; font-size: 13px;">Apply to the top-level folder and all its children (${impact.child_folders} folders, ${impact.child_files} files). This will remove custom file permissions so files inherit from the parent.</p>
                    </div>
                </div>
            `;
            const footer = `
                <button type="button" class="button button-secondary" id="vm-file-permission-cancel">Cancel</button>
                <button type="button" class="button button-primary" id="vm-file-permission-file-only">Apply to File Only</button>
                <button type="button" class="button button-primary" id="vm-file-permission-folder-tree">Apply to Folder Tree</button>
            `;

            // Snapshot current modal view so Cancel can restore it
            const $modal = (window as any).jQuery('#vm-file-settings-modal');
            const prevTitle = $modal.find('span.settings_modal_title').text();
            const prevContent = $modal.find('div.modal-form-content').html();
            const prevFooter = $modal.find('footer .inner').html();

            // Reuse the modal shell for a confirm step
            this.modal.init_modal(title, body, footer);

			return new Promise<'file_only' | 'folder_tree' | null>((resolve) => {
				const onFileOnly = () => {
					const btnEl = (window as any).jQuery('#vm-file-permission-file-only').get(0);
					if (btnEl) {
						this.spinnerController.toggleSpinnerOnButton(btnEl, true);
                        jQuery('#vm-file-permission-file-only').addClass('disabled');
					}
					cleanup();
					resolve('file_only');
				};
				const onFolderTree = () => {
					const btnEl = (window as any).jQuery('#vm-file-permission-folder-tree').get(0);
					if (btnEl) {
						this.spinnerController.toggleSpinnerOnButton(btnEl, true);
                        jQuery('#vm-file-permission-folder-tree').addClass('disabled');
					}
					cleanup();
					resolve('folder_tree');
				};
                const onCancel = () => {
                    // Restore previous modal content (preserves unsaved edits)
                    this.modal.init_modal(prevTitle, prevContent, prevFooter);
                    const btnEl = jQuery('#vm-file-permission-file-only, #vm-file-permission-folder-tree').get(0);
                    if (btnEl) {
                        this.spinnerController.toggleSpinnerOnButton(btnEl, false);
                    }
                    jQuery('#vm-file-permission-file-only, #vm-file-permission-folder-tree').removeClass('disabled');
                    // Also ensure main Save button is fully reset (spinner + disabled)
                    const saveBtn = jQuery('#vm-file-settings-modal #vm-drive-save-btn').get(0);
                    if (saveBtn) {
                        this.spinnerController.toggleSpinnerOnButton(saveBtn, false);
                        jQuery('#vm-file-settings-modal #vm-drive-save-btn')
                            .removeClass('disabled')
                            .removeClass('vm-spinner-wrap')
                            .removeAttr('disabled');
                    }
                    cleanup();
                    resolve(null);
                };
                const cleanup = () => {
                    (window as any).jQuery(document).off('click', '#vm-file-permission-file-only', onFileOnly);
                    (window as any).jQuery(document).off('click', '#vm-file-permission-folder-tree', onFolderTree);
                    (window as any).jQuery(document).off('click', '#vm-file-permission-cancel', onCancel);
                };
                (window as any).jQuery(document).on('click', '#vm-file-permission-file-only', onFileOnly);
                (window as any).jQuery(document).on('click', '#vm-file-permission-folder-tree', onFolderTree);
                (window as any).jQuery(document).on('click', '#vm-file-permission-cancel', onCancel);
            });
        }

		private async showPropagationPromptModal(parentFolderId: number): Promise<boolean> {
            const driveType = this.currentSettings?.drive_type || this.getCurrentDriveType();
            const impact = await this.fetchPropagationImpact(parentFolderId, driveType, this.currentSettings?.file_id);
            const title = 'Apply to Entire Tree?';
            const body = `
                <div class="vm-propagation-prompt">
                    <p>This file belongs to a higher-level folder. You can apply these rules to the absolute folder and propagate them to all its children.</p>
                    <p><strong>Affected children:</strong> ${impact.child_folders} folders, ${impact.child_files} files.</p>
                    <p>This will remove custom file permissions so files inherit from the parent.</p>
                </div>
            `;
            const footer = `
                <button type="button" class="button button-secondary" id="vm-propagate-cancel">Cancel</button>
                <button type="button" class="button button-primary" id="vm-propagate-apply">Apply to Entire Tree</button>
            `;

            // Snapshot current modal view so Cancel can restore it
            const $modal = (window as any).jQuery('#vm-file-settings-modal');
            const prevTitle = $modal.find('span.settings_modal_title').text();
            const prevContent = $modal.find('div.modal-form-content').html();
            const prevFooter = $modal.find('footer .inner').html();

            // Reuse the modal shell for a confirm step
            this.modal.init_modal(title, body, footer);

			return new Promise<boolean>((resolve) => {
				const onApply = () => {
					const btnEl = (window as any).jQuery('#vm-propagate-apply').get(0);
					if (btnEl) {
						this.spinnerController.toggleSpinnerOnButton(btnEl, true);
                        jQuery('#vm-propagate-apply').addClass('disabled');
					}
					cleanup();
					resolve(true);
				};
                const onCancel = () => {
                    // Restore previous modal content (preserves unsaved edits)
                    this.modal.init_modal(prevTitle, prevContent, prevFooter);
                    const btnEl = jQuery('#vm-propagate-apply').get(0);
                    if (btnEl) {
                        this.spinnerController.toggleSpinnerOnButton(btnEl, false);
                    }
                    jQuery('#vm-propagate-apply').removeClass('disabled');
                    // Also ensure main Save button is fully reset (spinner + disabled)
                    const saveBtn = jQuery('#vm-file-settings-modal #vm-drive-save-btn').get(0);
                    if (saveBtn) {
                        this.spinnerController.toggleSpinnerOnButton(saveBtn, false);
                        jQuery('#vm-file-settings-modal #vm-drive-save-btn')
                            .removeClass('disabled')
                            .removeClass('vm-spinner-wrap')
                            .removeAttr('disabled');
                    }
                    cleanup();
                    resolve(false);
                };
                const cleanup = () => {
                    (window as any).jQuery(document).off('click', '#vm-propagate-apply', onApply);
                    (window as any).jQuery(document).off('click', '#vm-propagate-cancel', onCancel);
                };
                (window as any).jQuery(document).on('click', '#vm-propagate-apply', onApply);
                (window as any).jQuery(document).on('click', '#vm-propagate-cancel', onCancel);
            });
        }

        private async applySettingsToParent(driveType: string, parentFolderId: number, settings: any): Promise<void> {
            const ajaxUrl = vmFileSettings?.ajaxUrl || '/wp-admin/admin-ajax.php';
            const nonce = vmFileSettings?.driveSettingsNonce || vmFileSettings?.nonce || '';
            await (window as any).jQuery.post(ajaxUrl, {
                action: 'apply_settings_to_parent',
                drive_type: driveType,
                parent_folder_id: parentFolderId,
                settings,
                nonce
            });
        }

        private getCurrentDriveType(): 'media_gallery' | 'user_files' | 'my_files' | 'org_drive' | 'shared' | 'trash' {
            // Prefer URL parameter
            const urlParams = new URLSearchParams(window.location.search);
            const drive = urlParams.get('drive');
            // Fallback to active tab data-drive if available
            const tabDrive = (jQuery('.vm-media-library-tabs .nav-tab-active').data('drive') as string) || '';
            const val = (drive || tabDrive || '').toString();
            if (val === 'user_files' || val === 'org_drive' || val === 'my_files' || val === 'shared' || val === 'trash') {
                return val as 'user_files' | 'org_drive' | 'my_files' | 'shared' | 'trash';
            }
            return 'media_gallery';
        }

        /**
         * Trash drive: true when browsing inside a trashed folder (not All Files / Unorganized).
         */
        private isAdminTrashInsideTrashedFolder(): boolean {
            if (this.getCurrentDriveType() !== 'trash') {
                return false;
            }
            const folderId = this.resolveAdminCurrentRmlFolderId();
            return folderId !== null && folderId > 0;
        }

        private resolveAdminCurrentRmlFolderId(): number | null {
            try {
                const rml = (window as any).rml;
                if (rml?.store?.selectedId !== undefined) {
                    const storeId = rml.store.selectedId;
                    if (storeId === 'all' || storeId === '' || storeId === null) {
                        return null;
                    }
                    const n = typeof storeId === 'number' ? storeId : parseInt(String(storeId), 10);
                    if (!isNaN(n) && n > 0) {
                        return n;
                    }
                    return null;
                }
            } catch (_) {}

            if ((window as any).vmCurrentRMLFolder && Number((window as any).vmCurrentRMLFolder) > 0) {
                return Number((window as any).vmCurrentRMLFolder);
            }

            try {
                const library = (window as any).wp?.media?.frame?.content?.get?.();
                const rf = library?.collection?.props?.get?.('rml_folder');
                if (rf !== undefined && rf !== null && rf !== '' && rf !== 'all') {
                    const n = parseInt(String(rf), 10);
                    if (!isNaN(n) && n > 0) {
                        return n;
                    }
                }
            } catch (_) {}

            const cookieMatch = document.cookie.match(/(?:^|;\s*)rml_folder=([^;]+)/);
            if (cookieMatch) {
                const n = parseInt(decodeURIComponent(cookieMatch[1]), 10);
                if (!isNaN(n) && n > 0) {
                    return n;
                }
            }

            return null;
        }

        private getModalFooter(): string {
            return `
                <button type="button" class="button button-secondary button-large custom-wc-modal-close" id="vm-file-cancel-btn">Cancel</button>
                <button type="button" class="button button-primary button-large" id="vm-drive-save-btn">Save</button>
            `;
        }

        private showLoading(): void {
            const loadingHtml = `
                <div class="vm-loading-spinner">
                    <div class="spinner"></div>
                    <p>${this.getString('loading', 'Loading...')}</p>
                </div>
            `;
            this.modal.init_modal('Loading...', loadingHtml, '');
        }

        private showError(message: string): void {
            const errorHtml = `
                <div class="vm-error-message">
                    <p>Error: ${message}</p>
                    <button type="button" class="vm-btn vm-btn-secondary" onclick="location.reload()">Reload</button>
                </div>
            `;
            this.modal.init_modal('Error', errorHtml, this.getModalFooter());
        }

        private showSuccess(message: string): void {
            const successHtml = `
                <div class="vm-success-message">
                    <p><span class="dashicons dashicons-yes-alt"></span> ${message}</p>
                </div>
            `;
            this.modal.init_modal('Success', successHtml, this.getModalFooter());
        }

        private showHierarchyError(message: string, restrictedParents: number[], errorData?: any): void {
            const currentFileId = this.currentSettings?.file_id;
            const isGeneralAccessOnly = errorData?.type === 'general_access_only';
            
            const errorHtml = `
                <div class="vm-hierarchy-error-message">
                    <p><span class="dashicons ${isGeneralAccessOnly ? 'dashicons-admin-site-alt3' : 'dashicons-lock'}"></span> 
                    <strong>${isGeneralAccessOnly ? 'General Access Restricted' : 'Permission Denied'}</strong></p>
                    <p>${message}</p>
                    ${isGeneralAccessOnly ? `
                        <div class="vm-general-access-note" style="background:#e7f3ff; padding:10px; border-left:4px solid #0073aa; margin:10px 0;">
                            <p><strong>Note:</strong> You can still add specific users, roles, and groups to "System Roles with Access" - this restriction only affects anonymous/public access through "General Access".</p>
                        </div>
                    ` : ''}
                    <div class="vm-hierarchy-actions">
                        <button type="button" class="button button-primary" onclick="window.fileSettingsModal.showFileParentFolder(${currentFileId})">
                            <span class="dashicons dashicons-admin-site-alt3"></span> View Parent Folder
                        </button>
                        <button type="button" class="button button-secondary vm-hierarchy-help">
                            <span class="dashicons dashicons-editor-help"></span> Help
                        </button>
                    </div>
                    <div class="vm-hierarchy-help-text" style="display:none; margin-top:15px; padding:10px; background:#f9f9f9; border-left:4px solid #0073aa;">
                        <h4>Solutions:</h4>
                        <ol>
                            ${isGeneralAccessOnly ? `
                                <li><strong>Use "System Roles with Access":</strong> Add specific users/roles instead - this works even with restricted parents</li>
                                <li><strong>Change Parent General Access:</strong> Set the parent folder's General Access to a less restrictive level</li>
                                <li><strong>Move Folder:</strong> Move this folder out from under the restricted parent</li>
                            ` : `
                                <li><strong>Change Parent Folder:</strong> Set the parent folder to a less restrictive access level first</li>
                                <li><strong>Use "System Roles with Access":</strong> Add specific users/roles instead of changing general access</li>
                                <li><strong>Move Folder:</strong> Move this folder out from under the restricted parent</li>
                            `}
                        </ol>
                    </div>
                </div>
            `;
            
            this.modal.init_modal(isGeneralAccessOnly ? 'General Access Restricted' : 'Permission Error', errorHtml, this.getModalFooter());
            
            // Add help toggle functionality
            setTimeout(() => {
                $('.vm-hierarchy-help').on('click', function() {
                    $('.vm-hierarchy-help-text').slideToggle();
                });
            }, 100);
        }

        // Public methods for external access
        public openForFile(fileId: number, driveType?: string): void {
            this.open(fileId, driveType);
        }

        public showFolderHierarchy(folderId: number): void {
            const ajaxUrl = vmFileSettings?.ajaxUrl || '/wp-admin/admin-ajax.php';
            const nonce = vmFileSettings?.nonce || '';

            $.post(ajaxUrl, {
                action: 'get_folder_hierarchy',
                file_id: folderId,
                nonce: nonce
            })
            .done((response: any) => {
                if (response.success) {
                    this.displayFolderHierarchy(response.data);
                } else {
                    this.showError(response.data || 'Failed to get folder hierarchy');
                }
            })
            .fail(() => {
                this.showError('Network error occurred while fetching folder hierarchy');
            });
        }

        private displayFolderHierarchy(hierarchyData: any): void {
            let hierarchyHtml = '<div class="vm-folder-hierarchy">';
            hierarchyHtml += '<h3>Folder Structure</h3>';
            
            if (hierarchyData.hierarchy && hierarchyData.hierarchy.length > 0) {
                hierarchyHtml += '<ul class="vm-hierarchy-list">';
                
                // Reverse to show from root to current
                const reversedHierarchy = [...hierarchyData.hierarchy].reverse();
                
                reversedHierarchy.forEach((folder: any, index: number) => {
                    const isRestricted = folder.access_level === 'restricted';
                    const isCurrent = folder.is_current;
                    const icon = isRestricted ? 'dashicons-lock' : 'dashicons-category';
                    const status = isRestricted ? 'RESTRICTED' : folder.access_level.toUpperCase();
                    const cssClass = isRestricted ? 'vm-restricted-folder' : 'vm-allowed-folder';
                    
                    hierarchyHtml += `
                        <li class="vm-hierarchy-item ${cssClass} ${isCurrent ? 'vm-current-folder' : ''}">
                            <span class="dashicons ${icon}"></span>
                            <strong>${folder.name}</strong> (ID: ${folder.id})
                            <span class="vm-access-badge vm-access-${folder.access_level}">${status}</span>
                            ${isCurrent ? '<span class="vm-current-badge">← Current Folder</span>' : ''}
                        </li>
                    `;
                });
                
                hierarchyHtml += '</ul>';
                
                if (hierarchyData.has_restricted_parents) {
                    hierarchyHtml += `
                        <div class="vm-hierarchy-warning">
                            <p><span class="dashicons dashicons-warning"></span> 
                            <strong>Issue:</strong> One or more parent folders are restricted, preventing broader access to child folders.</p>
                        </div>
                    `;
                }
            } else {
                hierarchyHtml += '<p>No hierarchy information available.</p>';
            }
            
            hierarchyHtml += '</div>';
            
            this.modal.init_modal('Folder Hierarchy', hierarchyHtml, this.getModalFooter());
        }
    }

    // Initialize when document is ready
    $(document).ready(() => {
        // Prevent multiple instances
        if ((window as any).fileSettingsModalInstance) {
            return;
        }
        
        // Make FileSettingsModal class available globally
        (window as any).FileSettingsModal = FileSettingsModal;
        
        // Initialize the modal (frontend and admin)
        const fileModal = new FileSettingsModal();
        (window as any).fileSettingsModal = fileModal;
        (window as any).fileSettingsModalInstance = fileModal;
    });

})(jQuery);